import {
  Flex,
  Box,
  Thead,
  Table,
  Th,
  Tr,
  Tbody,
  Button,
  HStack,
  Td,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  useDisclosure,
  Alert,
  AlertIcon,
  Text,
  Center,
  Icon,
  Tooltip,
  IconButton,
  useColorModeValue,
  Stack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Card,
  Badge,
  Input,
} from '@chakra-ui/react';
import { ArrowDownIcon, ArrowUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Certificate, CertificateRepository } from '../../../../api/CertificateRepository';
import { useLocation, useNavigate } from 'react-router-dom';
import { SearchBar } from 'components/navbar/searchBar/SearchBar';
import { IoMdArrowUp } from 'react-icons/io';
import { MdChevronLeft, MdChevronRight, MdDelete, MdVisibility } from 'react-icons/md';
import { useAuth } from 'contexts/authContext';

// Debounced input component for search and filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}: {
  value: string | number;
  onChange: (value: string | number) => void;
  debounce?: number;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  React.useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);
    return () => clearTimeout(timeout);
  }, [value, debounce, onChange]);

  return (
    <SearchBar
      {...props}
      value={value}
      onChange={(e: any) => setValue(e.target.value)}
      h="44px"
      w={{ lg: '390px' }}
      borderRadius="16px"
    />
  );
}


type SortKey = keyof Certificate;

export default function CertificateList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useAuth();

  const cellColor = useColorModeValue('secondaryGray.900', 'white');

  const headerTextStyle = {
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.6px',
    textTransform: 'uppercase' as const,
    color: 'gray.400',
  };

  const cellTextStyle = {
    fontSize: '15px',
    fontWeight: '500',
    color: cellColor,
  };

  const btncolor = useColorModeValue('navy.700', 'white');
  const textColor = useColorModeValue('blue.50', 'navy.700');
  const borderGray = useColorModeValue('gray.200', 'white');
  const bgcolor = useColorModeValue('white', 'navy.800')

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>('certificateId');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [globalFilter, setGlobalFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [assetFilter, setAssetFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [allVendors, setAllVendors] = useState<string[]>([]);
  const [allassets, setAllassets] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const savedPageIndex = location.state?.pageIndex;
  const [pageIndex, setPageIndex] = useState(savedPageIndex ?? 0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  //const [searchText, setSearchText] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  // Centered Alert state
  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<'success' | 'error'>('success');
  const [exportAlertVisible, setExportAlertVisible] = useState(false);

  // Fetch certificates

  const isFirstRender = useRef(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await CertificateRepository.getAllCertificate(
          pageIndex + 1,
          pageSize,
          vendorFilter,
          assetFilter,
          statusFilter,
          appliedSearch
        );

        const certificates = response.data || [];

        setCertificates(certificates);
        setHasMore(certificates.length === pageSize);
        setTotalPages(Math.ceil(response.totalCount / pageSize));

        // Generate filter options from same API response
        const vendors = [
          ...new Set(certificates.map(item => item.vendorName).filter(Boolean)),
        ];

        const assets = [
          ...new Set(certificates.map(item => item.assetName).filter(Boolean)),
        ];

        const statuses = [
          ...new Set(certificates.map(item => item.status).filter(Boolean)),
        ];

        setAllVendors(vendors);
        setAllassets(assets);
        setStatuses(statuses);

      } catch (error) {
        showCenterAlert("Failed to load certificates.", "error");
      }
    };

    fetchData();
  }, [
    pageIndex,
    pageSize,
    vendorFilter,
    assetFilter,
    statusFilter,
    appliedSearch
  ]);

  const createPages = () => {
    const pages = [];

    for (let i = 0; i <= pageIndex; i++) {
      pages.push(i + 1);
    }

    if (hasMore) {
      pages.push(pageIndex + 2);
    }

    return pages;
  };
  // Show centered alert 
  const showCenterAlert = (message: string, status: 'success' | 'error') => {
    setCenterAlertMessage(message);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    setTimeout(() => setCenterAlertVisible(false), 3000);
  };


  // Sorting logic
  const sortedCertificates = useMemo(() => {
    let data = [...certificates];
    data.sort((a, b) => {
      const aValue = a[sortKey] ?? '';
      const bValue = b[sortKey] ?? '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortOrder === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
    return data;
  }, [certificates, sortKey, sortOrder]);

  // Filtering logic

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  // delete logic
  const handleDelete = async (id: string) => {
    if (role === "DeptUser") return
    try {
      await CertificateRepository.deleteCertificate(id);
      setCertificates((prev) => prev.filter((cert) => cert.certificateId !== id));
      showCenterAlert('Certificate deleted successfully.', 'success');
    } catch (error) {

      showCenterAlert('Error deleting certificate.', 'error');
    }
  };

  const confirmDelete = (id: string) => {
    setSelectedId(id);
    onOpen();
  };

  const handleConfirmDelete = () => {
    if (selectedId) {
      handleDelete(selectedId);
    }
    onClose();
  };

  // export logic
  const handleExport = async () => {
    try {
      const response = await CertificateRepository.exportCertificate('csv', {
        search: searchFilter,
        vendorName: vendorFilter,
        assetName: assetFilter,
        status: statusFilter,
      });

      const blob = new Blob([response], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", "certificates.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setExportAlertVisible(true);

      setTimeout(() => {
        setExportAlertVisible(false);
      }, 3000);
    } catch (err) {
      showCenterAlert('Error exporting certificates.', 'error');
    }
  };

  const renderSortArrow = (key: SortKey) => {
    if (sortKey !== key) return null;
    return sortOrder === 'asc' ? <ArrowUpIcon ml={2} /> : <ArrowDownIcon ml={2} />;
  };

  return (
    <Flex direction="column" position={'sticky'} top="60px">
      <Box height="100vh" width="100%">
        <Flex direction="column" pt={{ sm: "125px", lg: "75px" }}>
          <Card px="0px" bg={bgcolor}>
            <Flex mb={2} p={2} position="sticky" top="10px" zIndex={50} align="center" justify="space-between">
              <HStack spacing={4}>
                <Input
                  color={"white"}
                  placeholder="Search..."
                  value={searchFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchFilter(value);

                    if (value.trim() === "") {
                      setAppliedSearch("");
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setAppliedSearch(searchFilter);
                    }
                  }}
                  bg={useColorModeValue("gray.100", "gray.700")}
                  width="50%"
                  borderRadius="full"
                />
                {/* Asset Filter */}
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="gray"
                    borderRadius="16px"
                    px={5}
                    h="44px"
                  >
                    {assetFilter || "Asset"}
                  </MenuButton>

                  <MenuList color={'white'} zIndex={1000} boxShadow="lg">
                    <MenuItem onClick={() => setAssetFilter("")}>All</MenuItem>
                    {allassets.map(a => (
                      <MenuItem key={a} onClick={() => setAssetFilter(a)}>
                        {a}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {/* Vendor Filter */}
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="gray"
                    borderRadius="16px"
                    px={5}
                    h="44px"
                  >
                    {vendorFilter || "Vendor"}
                  </MenuButton>

                  <MenuList color="white" zIndex={1000} boxShadow="lg" >
                    <MenuItem onClick={() => setVendorFilter("")}>All</MenuItem>
                    {allVendors.map(v => (
                      <MenuItem key={v} onClick={() => setVendorFilter(v)}>
                        {v}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
                {/* Status Filter */}
                <Menu>
                  <MenuButton
                    as={Button}
                    rightIcon={<ChevronDownIcon />}
                    colorScheme="gray"
                    borderRadius="16px"
                    px={5}
                    h="44px"
                  >
                    {statusFilter || "Status"}
                  </MenuButton>

                  <MenuList color={'white'} zIndex={1000} boxShadow="lg">
                    <MenuItem onClick={() => setStatusFilter("")}>All</MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Active")}>Active</MenuItem>
                    <MenuItem onClick={() => setStatusFilter("Inactive")}>Inactive</MenuItem>
                  </MenuList>
                </Menu>
              </HStack>
              <Button
                colorScheme="blue"
                variant="solid"
                marginLeft={16}
                fontWeight={'bold'}
                onClick={handleExport}
              >
                <Icon as={IoMdArrowUp} width="30px" height="20px" color="inherit" />
                Export
              </Button>
            </Flex>

            <Box overflowY="auto" >
              <Table
                variant="simple"
              >
                <Thead>
                  <Tr
                    fontSize={"14px"}
                    textTransform={"uppercase"}
                    fontWeight={"700"}
                    sx={{
                      '& th': {
                        textAlign: 'center',
                        ...headerTextStyle,
                      },
                    }}
                  >
                    <Th cursor="pointer" onClick={() => handleSort('certificateId')}>
                      Certificate Name {renderSortArrow('certificateId')}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('assetName')}>
                      Asset Name {renderSortArrow('assetName')}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('vendorName')}>
                      Vendor Name {renderSortArrow('vendorName')}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('uploadedAt')}>
                      Upload Date {renderSortArrow('uploadedAt')}
                    </Th>
                    <Th cursor="pointer" onClick={() => handleSort('status')}>
                      Status {renderSortArrow('status')}
                    </Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {certificates.length === 0 ? (
                    <Tr>
                      <Td colSpan={6}>
                        <Center py={10} flexDirection="column">
                          <Text fontSize="lg" fontWeight="600" color="gray.500" mb={3}>
                            No certificates found
                          </Text>
                        </Center>
                      </Td>
                    </Tr>
                  ) : (
                    sortedCertificates.map((cert) => (
                      <Tr
                        key={cert.certificateId}
                        sx={{
                          '& td': {
                            textAlign: 'center',
                            cursor: 'pointer',
                            ...cellTextStyle,
                          },
                          '&:hover td': { backgroundColor: textColor },
                        }}
                      >
                        <Td>{cert.certificateName}</Td>
                        <Td>{cert.assetName}</Td>
                        <Td>{cert.vendorName}</Td>
                        <Td>{cert.uploadedAt}</Td>
                        <Td>
                          <Badge
                            colorScheme={
                              cert.status?.toLowerCase() === "active"
                                ? "green"
                                : cert.status?.toLowerCase() === "expired"
                                  ? "red"
                                  : "gray"
                            }
                            fontSize="15px"
                            fontWeight="500"
                          >
                            {cert.status}
                          </Badge>
                        </Td>

                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="View details" placement="top" hasArrow>
                              <IconButton
                                aria-label="View"
                                icon={<MdVisibility size={20} />}
                                size="md"
                                variant="ghost"
                                color="blue.500"
                                _hover={{ bg: "blue.50", color: "blue.600" }}
                                _active={{ bg: "blue.100" }}
                                rounded="full"
                                onClick={() =>
                                  navigate("/admin/CertificateDetails", {
                                    state: { id: cert.certificateId, pageIndex: pageIndex },
                                  })
                                }
                              />
                            </Tooltip>

                            {role !== "DeptUser" && role !== "Vendor" && (
                              <Tooltip label="Delete certificate" placement="top" hasArrow>
                                <IconButton
                                  aria-label="Delete"
                                  icon={<MdDelete size={20} />}
                                  size="md"
                                  variant="ghost"
                                  color="red.500"
                                  _hover={{ bg: "red.50", color: "red.600" }}
                                  _active={{ bg: "red.100" }}
                                  rounded="full"
                                  onClick={() => confirmDelete(cert.certificateId)}
                                  isDisabled={role === "DeptUser" || role === "Vendor"}
                                />
                              </Tooltip>
                            )}
                          </HStack>
                        </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>

            {/* Delete confirmation  */}
            <AlertDialog
              isOpen={isOpen}
              leastDestructiveRef={cancelRef}
              onClose={onClose}
              isCentered
            >
              <AlertDialogOverlay>
                <AlertDialogContent>
                  <AlertDialogHeader fontSize="lg" fontWeight="bold">
                    Delete Certificate
                  </AlertDialogHeader>
                  <AlertDialogBody>
                    Are you sure you want to delete this certificate? This action cannot be undone.
                  </AlertDialogBody>
                  <AlertDialogFooter>
                    <Button ref={cancelRef} onClick={onClose}>
                      Cancel
                    </Button>
                    <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                      Delete
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialogOverlay>
            </AlertDialog>

            {/*  Centered Alert */}
            {exportAlertVisible && (
              <Center
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={60}
                pointerEvents="none"
              >
                <Alert
                  status="success"
                  variant="solid"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  width="600px"
                  borderRadius="14px"
                  boxShadow="xl"
                  py={10}
                  px={16}
                  pointerEvents="auto"
                >
                  <AlertIcon boxSize="40px" mr={0} />
                  <Text fontSize="lg" fontWeight="700" mt={1}>
                    Exported CSV successfully
                  </Text>
                </Alert>
              </Center>
            )}
            {centerAlertVisible && (
              <Center
                position="fixed"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex={60}
                pointerEvents="none"
              >
                <Alert
                  status={centerAlertStatus}
                  variant="solid"
                  opacity={1}
                  bg="red.500"
                  color="white"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  width="600px"
                  borderRadius="14px"
                  boxShadow="xl"
                  py={10}
                  px={16}
                  pointerEvents="auto"
                >
                  <AlertIcon boxSize="40px" mr={0} color="white" />

                  <Text fontSize="lg" fontWeight="700" mt={2}>
                    {centerAlertMessage}
                  </Text>
                </Alert>
              </Center>
            )
            }
          </Card>

          {globalFilter === "" && (
            <Flex w="100%" justify="space-between" px="20px" m={4}>

              {/* Page Size Dropdown */}
              <Menu>
                <Text m={2}>showing row per page</Text>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  colorScheme="gray"
                  borderRadius="16px"
                  px={5}
                  h="44px"
                >
                  {pageSize}
                </MenuButton>

                <MenuList zIndex={1000} boxShadow="lg">
                  {[10, 20].map((size) => (
                    <MenuItem
                      key={size}
                      onClick={() => {
                        setPageSize(size);
                        setPageIndex(0);
                      }}
                    >
                      {size}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {/* Pagination buttons */}
              <Stack direction="row" alignSelf="flex-end" spacing="4px" ms="auto">
                <Button
                  variant="no-effects"
                  onClick={() => setPageIndex((prev: any) => Math.max(0, prev - 1))}
                  disabled={pageIndex === 0}
                  w="40px"
                  h="40px"
                  borderRadius="50%"
                  bg="transparent"
                  border="1px solid"
                  borderColor={borderGray}
                >
                  <Icon as={MdChevronLeft} w="16px" h="16px" color={btncolor} />
                </Button>

                {/* Page numbers */}
                {globalFilter === "" &&
                  createPages().map((num: number, index: number) => (
                    <Button
                      key={index}
                      variant="no-effects"
                      onClick={() => setPageIndex(num - 1)}
                      w="40px"
                      h="40px"
                      borderRadius="50%"
                      bg={num === pageIndex + 1 ? 'blue.400' : 'transparent'}
                      border={num === pageIndex + 1 ? 'none' : '1px solid lightgray'}
                      _hover={{
                        bg: num === pageIndex + 1 ? 'blue.300' : 'whiteAlpha.100',
                      }}
                    >
                      <Text fontSize="sm" color={num === pageIndex + 1 ? '#fff' : btncolor}>
                        {num}
                      </Text>
                    </Button>
                  ))}

                {/* Next */}
                <Button
                  variant="no-effects"
                  onClick={() => setPageIndex((prev: any) => prev + 1)}
                  disabled={!hasMore || globalFilter !== ""}
                  w="40px"
                  h="40px"
                  borderRadius="50%"
                  bg="transparent"
                  border="1px solid"
                  borderColor={borderGray}
                >
                  <Icon as={MdChevronRight} w="16px" h="16px" color={btncolor} />
                </Button>
              </Stack>

            </Flex>
          )}
        </Flex>

      </Box>

    </Flex >
  );
}
